import {
  HttpAnswer,
  HttpQuestion,
  HttpSurvey,
  ISurveyParser,
} from "../surveyRepository.types";
import { err, ok, Result } from "neverthrow";
import {
  IAnswer,
  IAnswerRequestData,
  IQuestion,
  ISurvey,
  ISurveyRequest,
} from "@/domain/entities/survey/survey.types";
import { ParseError } from "@/repositories/shared/errors/parseError";
import { Survey } from "@/domain/entities/survey/survey";
import { injectable } from "inversify";

function parseAnswers(answers?: HttpAnswer[]): IAnswer[] | undefined {
  const answerList = answers?.map((answer: HttpAnswer): IAnswer => {
    return {
      id: answer.uid,
      title: answer.displayText,
      order: answer.displayOrder,
    };
  });
  return answerList;
}

function parseQuestions(questions: HttpQuestion[]): IQuestion[] {
  const questionList = questions?.map((question: HttpQuestion): IQuestion => {
    return {
      order: question.no,
      answerType: question.type === "option" ? "radio" : question.type,
      title: question.label,
      answers: parseAnswers(question.options),
      maxLength: question.maxLength,
      placeholder: question.placeholder,
      datasource: question.datasource,
      valueField: question.valueField,
    };
  });
  return questionList;
}

function parseAnswerRequestData(
  questionOrder: number,
  answer: Map<string, string | string[]>
): IAnswerRequestData {
  const selected = answer.get("selected");
  const inputValue = answer.get("inputValue");
  const answerData: IAnswerRequestData = {
    no: questionOrder,
    selected: selected,
    inputValue: inputValue,
  };
  return answerData;
}

@injectable()
export class SurveyParser implements ISurveyParser {
  constructor() {}

  toDomain(data: HttpSurvey): Result<ISurvey, ParseError> {
    try {
      const surveyData: ISurvey = {
        id: data.surveyUid ?? undefined,
        title: data.title ?? undefined,
        questions: parseQuestions(data.questionProps ?? []),
      };
      const survey = new Survey(surveyData);
      return ok(survey);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }

  fromDomain(
    answers: Map<number, Map<string, string | string[]>>,
    surveyId?: string,
    allocUid?: string
  ): ISurveyRequest {
    const answerDataList = [];

    for (const key of answers.keys()) {
      const answerData = parseAnswerRequestData(key, answers.get(key)!);
      if (!answerData.selected && !answerData.inputValue) continue;
      answerDataList.push(answerData);
    }

    const surveyRequest: ISurveyRequest = {
      allocUid: allocUid ? allocUid : undefined,
      surveyUid: surveyId ? surveyId : undefined,
      answers: answerDataList.length ? answerDataList : undefined,
    };

    return surveyRequest;
  }
}
